import { useState } from 'react'
import * as Yup from 'yup'
import ClearIcon from '@mui/icons-material/Clear'
import { Form, Formik } from 'formik'
import { sileo } from 'sileo'
import tituloLoginSII from '../../../../../assets/tituloLoginSII.png'
import { BusinessAPI } from '../../../../../api/business.api'
import { encryptWithSecret } from '../../../../../utils/encrypt.util'
import { FieldWithErrorMessage } from '../../../../modules/ui/FieldWithErrorMsg'
import { Modal } from '../../../../modules/ui/Modal'
import CustomButton from '../../../../modules/ui/CustomButton'
import { formatRutForBackend } from '../../../../utils/formatter'

const schemaValidation = Yup.object().shape({
  user: Yup.string().required('Ingresa tu RUT'),
  password: Yup.string().required('Ingresa tu clave tributaria')
})

export const UpdateSiiCredentialsModal = ({ isOpen, onClose, currentBusiness }) => {
  const businessAPI = new BusinessAPI()
  const [isBusy, setIsBusy] = useState(false)

  const isEncryptionUnavailableError = error =>
    error?.message === 'ENCRYPT_SECRET no configurado' || error?.message === 'Web Crypto no disponible'

  const shouldRetryLegacyPayload = error => {
    const status = error?.response?.status
    return status === 500 || status === 502 || status === 503 || status === 504
  }

  const buildPayload = async values => {
    const normalizedRut = formatRutForBackend(values.user).trim()
    const cleanPassword = String(values.password || '').trim()
    const legacyPayload = {
      user: normalizedRut,
      password: cleanPassword
    }

    const encryptSecret = typeof window !== 'undefined' ? window.__APP_CONFIG__?.encryptSecret : ''
    if (!encryptSecret || !globalThis.crypto?.subtle) {
      return { legacyPayload, encryptedPayload: null }
    }

    try {
      const [encryptedUser, encryptedPassword] = await Promise.all([
        encryptWithSecret(normalizedRut, encryptSecret),
        encryptWithSecret(cleanPassword, encryptSecret)
      ])

      return {
        legacyPayload,
        encryptedPayload: {
          user: encryptedUser,
          password: encryptedPassword
        }
      }
    } catch (error) {
      if (isEncryptionUnavailableError(error)) {
        return { legacyPayload, encryptedPayload: null }
      }
      throw error
    }
  }

  const handleClose = () => {
    if (isBusy) return
    onClose?.()
  }

  const handleSubmit = async (values, actions) => {
    if (!currentBusiness?.id) {
      sileo.error({
        title: 'Empresa no disponible',
        description: 'No encontramos una empresa activa para actualizar sus credenciales.'
      })
      actions.setSubmitting(false)
      return
    }

    setIsBusy(true)

    try {
      const { legacyPayload, encryptedPayload } = await buildPayload(values)
      let response

      if (encryptedPayload) {
        try {
          response = await businessAPI.createBusiness(encryptedPayload, currentBusiness.id)
        } catch (error) {
          if (!shouldRetryLegacyPayload(error)) {
            throw error
          }
          response = await businessAPI.createBusiness(legacyPayload, currentBusiness.id)
        }
      } else {
        response = await businessAPI.createBusiness(legacyPayload, currentBusiness.id)
      }

      if (response?.status !== 200 && response?.status !== 201) {
        throw new Error('No se pudieron actualizar las credenciales')
      }

      sileo.success({
        title: 'Credenciales actualizadas',
        description: (
          <span className='text-green-500/80 font-medium'>
            Las credenciales SII se actualizaron correctamente.
          </span>
        )
      })

      actions.resetForm()
      setIsBusy(false)
      actions.setSubmitting(false)
      onClose?.()
    } catch (error) {
      console.error('Error actualizando credenciales SII:', error)
      sileo.error({
        title: 'No se pudieron actualizar las credenciales',
        description: 'Revisa el RUT y la clave tributaria e inténtalo nuevamente.'
      })
      setIsBusy(false)
      actions.setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className='flex max-h-[90vh] w-[min(42rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl bg-white shadow-2xl'>
        <div className='flex items-center justify-between bg-primary px-2 py-1 text-white sm:px-8'>
          <div>
            <p className='text-[11px] font-semibold uppercase tracking-[0.22em] text-white/70'></p>
            <h2 className='mt-1 text-xl font-bold sm:text-2xl'></h2>
          </div>

          <button
            type='button'
            onClick={handleClose}
            className='rounded-full p-2 text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50'
            disabled={isBusy}
            aria-label='Cerrar modal'
          >
            <ClearIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <Formik
          validationSchema={schemaValidation}
          initialValues={{ user: '', password: '' }}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className='flex min-h-0 flex-1 flex-col'>
              <div className='flex-1 overflow-y-auto px-6 py-8 sm:px-8'>
                <div className='mx-auto max-w-md'>
                  <p className='mb-5 text-center text-sm font-sans leading-6 text-slate-600'>
                    Ingresa nuevamente las credenciales tributarias para sincronizar esta empresa
                    con el SII.
                  </p>

                  <div className='overflow-hidden rounded-2xl border border-slate-200 shadow-md'>
                    <div className='flex h-[74px] items-center justify-center bg-primary px-6 py-3'>
                      <img src={tituloLoginSII} alt='tituloLoginSII' />
                    </div>

                    <ul className='flex flex-col gap-3 px-6 pb-6 pt-5'>
                      <li>
                        <FieldWithErrorMessage
                          type='text'
                          name='user'
                          label='RUT'
                          placeholder='Ej: 123456789'
                        />
                      </li>
                      <li>
                        <FieldWithErrorMessage
                          type='password'
                          name='password'
                          label='Clave Tributaria'
                          placeholder='Ingresa tu clave tributaria'
                        />
                      </li>
                      <li>
                        <button
                          disabled={isSubmitting || !currentBusiness?.id}
                          type='submit'
                          className='btn-primary w-full text-sm disabled:cursor-not-allowed disabled:opacity-50'
                        >
                          {isSubmitting ? 'ACTUALIZANDO...' : 'ACTUALIZAR CREDENCIALES'}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className='flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8'>
                <CustomButton
                  type='button'
                  title='Volver'
                  onClick={handleClose}
                  color='primary'
                  className='rounded-full px-5 py-2'
                  disabled={isBusy}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}
